import { Navigate, useParams } from 'react-router-dom';
import { InventoryRow } from '../components/inventory/InventoryRow';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  FAMILY_TITLES,
  FAMILY_SUBTITLES,
  isFamily,
  modulesFor,
  topicsListedIn,
} from '../config/moduleCatalog';

/**
 * The inventory page (feature 023, US4) — `/inventory/:family`.
 *
 * One list of every topic in the family, per `inventory.html`. The catalog in
 * `config/moduleCatalog.ts` is the only source of rows, so precision and
 * competitive share this page and differ only by title and module list
 * (FR-009, FR-013).
 */
export function InventoryPage() {
  const { family } = useParams<{ family: string }>();

  // An unknown family is a bad URL, not an error state worth a page — send the
  // user to the precision inventory, which is what the dashboard door points at.
  if (!isFamily(family)) {
    return <Navigate to="/inventory/precision" replace />;
  }

  return <InventoryContent family={family} />;
}

function InventoryContent({ family }: { family: 'precision' | 'competitive' }) {
  const title = FAMILY_TITLES[family];
  usePageTitle(`${family === 'precision' ? 'Precision' : 'Competitive'} inventory — Hawkes Bridge`);

  // Feature 033: a topic may carry its own module list (Preempts lists two modules under
  // precision and three under competitive) and may be listed in one inventory only, so the
  // rows and their counts come from the catalog per topic rather than per family.
  const topics = topicsListedIn(family);

  return (
    <div className="mx-auto w-full max-w-[1080px] px-4 py-8">
      <header className="mb-6">
        <h1 className="font-serif text-[26px] text-ink">{title}</h1>
        <p className="mt-1.5 text-[14.5px] text-ink-soft">{FAMILY_SUBTITLES[family]}</p>
        <div className="mt-2.5 h-0.5 w-16 bg-brass" aria-hidden="true" />
      </header>

      <div className="flex flex-col gap-2.5" data-testid="inventory-list">
        {topics.map((topic) => (
          <InventoryRow
            key={topic.key}
            topic={topic}
            to={`/inventory/${family}/${topic.key}`}
            moduleCount={modulesFor(family, topic).length}
          />
        ))}
      </div>
    </div>
  );
}
